import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { t } from "../../i18n/translations";
import { toast } from "sonner";

const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function AdminAvailability() {
  const { lang } = useLang();
  const [settings, setSettings] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/settings"); setSettings(data || {}); };
  useEffect(() => { load(); }, []);

  if (!settings) return <div className="p-10 text-ink-muted">…</div>;

  const wh = settings.working_hours || {};

  const setDayHours = (d, idx, value) => {
    const cur = wh[d] || [];
    const next = cur.length === 2 ? [...cur] : ["09:00", "17:00"];
    next[idx] = value;
    setSettings({ ...settings, working_hours: { ...wh, [d]: next } });
  };
  const toggleDay = (d) => {
    const cur = wh[d];
    const next = cur && cur.length === 2 ? [] : ["09:00", "17:00"];
    setSettings({ ...settings, working_hours: { ...wh, [d]: next } });
  };

  const save = async () => {
    try {
      const body = {
        business_name: settings.business_name || "Ayurveda Aargau",
        address: settings.address || "",
        phone: settings.phone || "",
        email: settings.email || "",
        working_hours: settings.working_hours || {},
        blocked_dates: settings.blocked_dates || [],
        slot_step_minutes: parseInt(settings.slot_step_minutes) || 30,
        buffer_minutes: parseInt(settings.buffer_minutes) || 30,
        intro_de: settings.intro_de || "",
        intro_fr: settings.intro_fr || "",
        intro_it: settings.intro_it || "",
        intro_en: settings.intro_en || "",
      };
      await api.put("/admin/settings", body);
      toast.success("✓");
      load();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-4xl" data-testid="admin-availability-page">
      <h1 className="font-display text-3xl sm:text-4xl text-ink">{t(lang, "admin.availability")}</h1>
      <p className="mt-3 text-ink-muted text-sm max-w-xl">Lege Arbeitszeiten und geblockte Tage fest. Diese Zeiten werden im Buchungsflow verwendet.</p>

      <div className="mt-8 bg-white border border-border rounded-md p-6">
        <h2 className="font-display text-2xl text-ink mb-5">{t(lang, "admin.working_hours")}</h2>
        <div className="space-y-3">
          {days.map((d) => {
            const open = (wh[d] || []).length === 2;
            return (
              <div key={d} className="grid grid-cols-2 sm:grid-cols-12 items-center gap-2 sm:gap-3 py-2 border-b border-border last:border-b-0" data-testid={`avail-row-${d}`}>
                <div className="sm:col-span-3 text-ink font-medium">{t(lang, `days.${d}`)}</div>
                <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm text-ink-muted justify-end sm:justify-start">
                  <input type="checkbox" checked={open} onChange={() => toggleDay(d)} data-testid={`avail-open-${d}`} /> Offen
                </label>
                <input type="time" disabled={!open} value={open ? wh[d][0] : "09:00"} onChange={(e) => setDayHours(d, 0, e.target.value)} className="col-span-1 sm:col-span-3 px-3 py-2 border border-border rounded-md disabled:bg-muted disabled:text-ink-muted/40" data-testid={`avail-from-${d}`} />
                <span className="hidden sm:block sm:col-span-1 text-center text-ink-muted">–</span>
                <input type="time" disabled={!open} value={open ? wh[d][1] : "17:00"} onChange={(e) => setDayHours(d, 1, e.target.value)} className="col-span-1 sm:col-span-3 px-3 py-2 border border-border rounded-md disabled:bg-muted disabled:text-ink-muted/40" data-testid={`avail-to-${d}`} />
              </div>
            );
          })}
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-muted">{t(lang, "admin.slot_step")}</span>
            <input type="number" value={settings.slot_step_minutes || 30} onChange={(e) => setSettings({ ...settings, slot_step_minutes: e.target.value })} className="mt-2 w-full px-3 py-2 border border-border rounded-md" data-testid="avail-slot-step" />
            <span className="text-[11px] text-ink-muted">Wie eng die Slots im Buchungsflow angezeigt werden (z. B. 30 = alle 30 Min eine Option).</span>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-muted">Pufferzeit nach Massage (Minuten)</span>
            <input type="number" value={settings.buffer_minutes ?? 30} onChange={(e) => setSettings({ ...settings, buffer_minutes: e.target.value })} className="mt-2 w-full px-3 py-2 border border-border rounded-md" data-testid="avail-buffer" />
            <span className="text-[11px] text-ink-muted">Vorbereitungszeit zwischen zwei Buchungen (Standard 30 Min).</span>
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-widest text-ink-muted">{t(lang, "admin.blocked_dates")}</span>
            <input value={(settings.blocked_dates || []).join(", ")} onChange={(e) => setSettings({ ...settings, blocked_dates: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="mt-2 w-full px-3 py-2 border border-border rounded-md" data-testid="avail-blocked" placeholder="2026-03-15, 2026-04-01" />
          </label>
        </div>

        <button onClick={save} className="mt-8 px-6 py-2.5 bg-terracotta text-white rounded-full hover:bg-terracotta-hover" data-testid="avail-save">{t(lang, "admin.save")}</button>
      </div>
    </div>
  );
}
